// File: Components/Structural/Sidebar/SidebarItem.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import { FaBookOpen, FaTag, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import './css/sidebarItem.css';

const SidebarItem = ({
  item,
  activeTab,
  handleItemClick,
  isExpanded,
  activeColor,
  discountPercentage, // Accept the discount percentage prop
  customContent,// Accept custom content prop
  background = false
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const IconComponent = item.icon;

  const handleItemClickWrapper = (id) => {
    handleItemClick(id);
  };

  // If the route is the current active tab
  const isActive = activeTab === item.id;

  // For the doc icon click
  const handleDocClick = (e, docLink) => {
    e.stopPropagation(); // Prevent triggering the main item click
    window.open(docLink, '_blank');
  };

  // Ensure we always have a tooltip title, fallback to item id if no title
  const tooltipTitle = item.title || item.id;

  return (
    <motion.div
      className={`${background ? "sidebar-color-gold" : ""} sidebar-item-container`}

      layout
      data-tutorial-target={item.id}   /* For tutorial targeting */
    >
      <motion.div
        layout
        className={clsx('sidebar-item', {
          'sidebar-item__active': isActive,
        })}
        onClick={() => handleItemClickWrapper(item.id)}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        // Add title attribute for native browser tooltip as fallback
        title={!isExpanded ? tooltipTitle : ''}
      >
        {isActive && (
          <motion.div
            layoutId="sidebar-item-indicator"
            className="sidebar-item__active-bg"
          />
        )}
        <div className="sidebar-item__content">
          <span
            className="sidebar-item__icon"
            style={isActive ? { color: activeColor } : {}}
          >
            <IconComponent />
          </span>

          <AnimatePresence>
            {isExpanded && (
              // Wrap text and tag in a container for flex layout
              <motion.span
                className="sidebar-item__text-container"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.3 }}
              >
                {/* Item Title */}
                <span className="sidebar-item__text">{tooltipTitle}</span>

                {/* Conditionally render the discount tag */}
                {discountPercentage > 0 && (
                  <span className="sidebar-item__discount-tag">
                    <FaTag /> {discountPercentage}%
                  </span>
                )}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Expandable arrow for sections */}
          {isExpanded && item.isExpandable && (
            <motion.span
              className="sidebar-item__expand-icon"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {item.isExpanded ? <FaChevronDown /> : <FaChevronRight />}
            </motion.span>
          )}

          {/* Documentation icon: only when expanded AND hovered */}
          {item.docLink && isExpanded && isHovered && (
            <motion.span
              className="sidebar-item__doc-icon"
              onClick={(e) => handleDocClick(e, item.docLink)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <FaBookOpen />
            </motion.span>
          )}

          {/* Tooltip when not expanded - use customContent if provided, otherwise default tooltip */}
          {!isExpanded && (
            customContent || <span className="tooltip">{item.title}</span>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SidebarItem;